import {
  ArchiveIcon,
  ArrowCounterClockwiseIcon,
  ArrowUpRightIcon,
  CaretDownIcon,
  CopyIcon,
  LockSimpleIcon,
  PlusIcon,
} from "@phosphor-icons/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useState } from "react";
import ScheduledBriefingsModals from "@/components/scheduled-briefings/modals/ScheduledBriefingsModals";
import { Button, LinkButton } from "@/components/ui/button";
import { InlineSearchField } from "@/components/ui/controls/searchfield";
import {
  TemplateThumbnailCard,
  TemplateThumbnailCardMenu,
} from "@/components/ui/report/TemplateThumbnailCard/TemplateThumbnailCard";
import { toast } from "@/components/ui/toast";
import useFeature from "@/lib/feature-management/useFeature";
import { setGraphQLTemplateLayoutArchived } from "@/lib/template-studio/repository/graphqlTemplateLayoutRepository";
import { useTemplateLayoutList } from "@/lib/template-studio/repository/useTemplateLayoutList";
import { templateMatchesSearch } from "@/lib/template-studio/templateLayoutSearch";
import { TEMPLATE_CREATE_PATH } from "@/lib/template-studio/templateStudioEntry";
import { cn } from "@/lib/utils";
import { ScheduledBriefingsTabPageHeader } from "@/routes/scheduled-briefings/ScheduledBriefingsTabPageHeader";
import type { TemplateLayoutSummary } from "@/types/template-layout/templateLayout.types";

const TEMPLATES_LANDING_DESCRIPTION =
  "Layouts built and maintained by the Handraise editorial team.";
const TEMPLATES_LANDING_POPULATED_DESCRIPTION =
  "Layouts built and maintained by the Handraise editorial team. Configure a briefing on top of one — the structure stays fixed so every edition looks the same.";

const templateUpdatedLabel = (updatedAt: string) =>
  `Updated ${format(new Date(updatedAt), "MMM d yyyy")}`;

const TEMPLATE_GRID_CLASS =
  "grid grid-cols-1 items-stretch gap-8 py-8 sm:grid-cols-[repeat(auto-fill,350px)]";

const TEMPLATE_LOADING_SKELETON_VISIBLE_ON_SMALL = 2;
const TEMPLATE_LOADING_SKELETON_KEYS = [
  "template-loading-skeleton-1",
  "template-loading-skeleton-2",
  "template-loading-skeleton-3",
  "template-loading-skeleton-4",
] as const;

interface NewTemplateLinkProps {
  variant: "default" | "outline";
}

const NewTemplateLink = ({ variant }: NewTemplateLinkProps) => (
  <LinkButton
    to={TEMPLATE_CREATE_PATH}
    variant={variant}
    size="sm"
    className="h-8 gap-2"
  >
    <PlusIcon
      aria-hidden
      className={cn("size-4", variant === "outline" && "text-gray-600")}
    />
    New template
  </LinkButton>
);

const TemplatesLandingRoute = () => {
  const canCreateTemplate = useFeature("can-create-templates", false);
  const canArchiveTemplates = useFeature(
    "enable-scheduled-briefings-v1-1",
    false,
  );
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const { items, isLoading, error, isRetryable, retry, setItemStatus } =
    useTemplateLayoutList();
  const [templateToArchive, setTemplateToArchive] =
    useState<TemplateLayoutSummary | null>(null);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  // GrowthBook can turn the flag off mid-session; drop any open confirmation.
  if (!canArchiveTemplates && templateToArchive !== null) {
    setTemplateToArchive(null);
  }
  const [searchTerm, setSearchTerm] = useState("");
  const [isArchivedExpanded, setIsArchivedExpanded] = useState(true);
  const templates = canArchiveTemplates
    ? items
    : items.filter((template) => template.status !== "archived");
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const visibleTemplates = templates.filter((template) =>
    templateMatchesSearch(template, normalizedSearchTerm),
  );
  const activeTemplates = visibleTemplates.filter(
    (template) => template.status !== "archived",
  );
  const archivedTemplates = visibleTemplates.filter(
    (template) => template.status === "archived",
  );
  const hasTemplates = templates.length > 0;
  const showPopulatedDescription = !isLoading && !error && hasTemplates;
  const templateStatusLabel = error
    ? "Templates unavailable"
    : `Active (${activeTemplates.length})`;
  const contentState = isLoading
    ? "loading"
    : error && !hasTemplates
      ? "error"
      : hasTemplates
        ? "populated"
        : "empty";
  const contentTransition = { duration: reduceMotion ? 0 : 0.18 };

  const setTemplateArchived = async (
    template: TemplateLayoutSummary,
    archived: boolean,
  ) => {
    if (isChangingStatus || !canArchiveTemplates) return;
    setIsChangingStatus(true);
    const result = await setGraphQLTemplateLayoutArchived(
      template.id,
      archived,
    );
    setIsChangingStatus(false);
    setTemplateToArchive(null);

    if (!result.success) {
      toast({
        type: "error",
        title: archived
          ? "Template couldn't be archived"
          : "Template couldn't be unarchived",
        description: result.error.message,
      });
      return;
    }

    setItemStatus(template.id, archived ? "archived" : "published");
    toast({
      type: "success",
      title: archived ? "Template archived" : "Template unarchived",
    });
  };

  const renderTemplateCard = (template: TemplateLayoutSummary) => {
    const canChangeStatus =
      canArchiveTemplates && canCreateTemplate && template.canEdit;
    const isArchived = template.status === "archived";

    return (
      <li
        key={template.id}
        className="group/template-card relative flex min-w-0"
      >
        <Link
          to="/scheduled-briefings/templates/$templateId"
          params={{ templateId: template.id }}
          className="flex h-full min-w-0 flex-1 cursor-pointer rounded-xl no-underline outline-hidden focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
        >
          <TemplateThumbnailCard
            className="h-full"
            isInteractive
            isArchived={isArchived}
            name={template.name}
            description={template.description}
            updatedLabel={templateUpdatedLabel(template.updatedAt)}
            slotCount={template.slotCount}
            briefingCount={template.briefingCount}
          />
        </Link>
        <TemplateThumbnailCardMenu
          templateName={template.name}
          items={[
            {
              id: "duplicate",
              icon: <CopyIcon className="size-4 text-gray-600" />,
              label: "Duplicate template",
              condition: canCreateTemplate,
              onClick: () =>
                void navigate({
                  to: TEMPLATE_CREATE_PATH,
                  search: { duplicateFrom: template.id },
                }),
            },
            {
              id: "details",
              icon: <ArrowUpRightIcon className="size-4 text-gray-600" />,
              label: "View template details",
              onClick: () =>
                void navigate({
                  to: "/scheduled-briefings/templates/$templateId",
                  params: { templateId: template.id },
                  search: { view: "details" },
                }),
            },
            {
              id: "archive",
              separator: true,
              icon: <ArchiveIcon className="size-4 text-gray-600" />,
              label: "Archive template",
              condition: canChangeStatus && !isArchived,
              isDisabled: isChangingStatus,
              onClick: () => setTemplateToArchive(template),
            },
            {
              id: "unarchive",
              separator: true,
              icon: (
                <ArrowCounterClockwiseIcon className="size-4 text-gray-600" />
              ),
              label: "Unarchive template",
              condition: canChangeStatus && isArchived,
              isDisabled: isChangingStatus,
              onClick: () => void setTemplateArchived(template, false),
            },
          ]}
        />
      </li>
    );
  };
  const renderContent = () => {
    switch (contentState) {
      case "loading":
        return (
          <ul
            className={TEMPLATE_GRID_CLASS}
            aria-live="polite"
            aria-busy="true"
          >
            {TEMPLATE_LOADING_SKELETON_KEYS.map((key, index) => (
              <li
                key={key}
                className={cn(
                  "flex min-w-0",
                  index >= TEMPLATE_LOADING_SKELETON_VISIBLE_ON_SMALL &&
                    "hidden sm:block",
                )}
              >
                <TemplateThumbnailCard isLoading />
              </li>
            ))}
          </ul>
        );
      case "error":
        return (
          <div
            className="flex flex-col items-center gap-3 py-16 text-center"
            role="alert"
            aria-live="polite"
          >
            <p className="max-w-md text-sm text-gray-700">{error?.message}</p>
            {isRetryable ? (
              <Button variant="outline" size="sm" onPress={retry}>
                Try again
              </Button>
            ) : null}
          </div>
        );
      case "populated":
        if (visibleTemplates.length === 0) {
          return (
            <p className="py-8 text-sm text-gray-700">
              No templates match your search. Try a different name or
              description.
            </p>
          );
        }
        return (
          <>
            {activeTemplates.length > 0 ? (
              <ul className={cn(TEMPLATE_GRID_CLASS, "pt-4")}>
                {activeTemplates.map(renderTemplateCard)}
              </ul>
            ) : null}
            {archivedTemplates.length > 0 ? (
              <section
                aria-labelledby="archived-templates-toggle"
                className="pb-8"
              >
                <div className="flex items-center gap-4 py-4">
                  <button
                    id="archived-templates-toggle"
                    type="button"
                    aria-expanded={isArchivedExpanded}
                    aria-controls="archived-templates-grid"
                    onClick={() =>
                      setIsArchivedExpanded((expanded) => !expanded)
                    }
                    className="flex cursor-pointer items-center gap-1.5 rounded text-xs font-medium tracking-[0.12px] text-gray-600 outline-hidden focus-visible:ring-2 focus-visible:ring-violet-500"
                  >
                    Archived ({archivedTemplates.length})
                    <CaretDownIcon
                      aria-hidden
                      className={cn(
                        "size-3.5 transition-transform",
                        !isArchivedExpanded && "-rotate-90",
                      )}
                    />
                  </button>
                  <div aria-hidden className="h-px flex-1 bg-gray-200" />
                </div>
                {isArchivedExpanded ? (
                  <ul
                    id="archived-templates-grid"
                    className={cn(TEMPLATE_GRID_CLASS, "pt-0 pb-0")}
                  >
                    {archivedTemplates.map(renderTemplateCard)}
                  </ul>
                ) : null}
              </section>
            ) : null}
          </>
        );
      case "empty":
        return (
          <div className="flex justify-center pt-[86px]">
            <div className="flex flex-col items-start gap-3 text-left">
              <h2 className="text-base font-medium text-gray-900">
                No templates yet
              </h2>
              <p className="text-sm text-gray-700">
                Templates will appear here once they&apos;ve been created by
                admin.
              </p>
              {canCreateTemplate ? <NewTemplateLink variant="outline" /> : null}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex min-h-full shrink-0 flex-col">
      <ScheduledBriefingsTabPageHeader
        title="Templates"
        description={
          showPopulatedDescription
            ? TEMPLATES_LANDING_POPULATED_DESCRIPTION
            : TEMPLATES_LANDING_DESCRIPTION
        }
        actions={
          <div className="flex w-full flex-col items-stretch gap-3 sm:w-89 sm:items-end">
            {canCreateTemplate ? <NewTemplateLink variant="default" /> : null}
            {hasTemplates ? (
              <InlineSearchField
                ariaLabel="Search templates"
                clearAriaLabel="Clear template search"
                value={searchTerm}
                onValueChange={setSearchTerm}
              />
            ) : null}
          </div>
        }
      />

      <section className="flex min-h-0 flex-1 flex-col bg-gray-50 px-16">
        <p role="status" className="sr-only">
          {normalizedSearchTerm && !isLoading
            ? `${activeTemplates.length} active and ${archivedTemplates.length} archived templates match your search.`
            : ""}
        </p>
        <div className="min-h-12 pt-6 pb-2">
          <AnimatePresence initial={false}>
            {isLoading ? null : (
              <motion.div
                className="flex items-center gap-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={contentTransition}
              >
                <p className="text-xs font-medium tracking-[0.12px] text-gray-600">
                  {templateStatusLabel}
                </p>
                <div aria-hidden className="h-px flex-1 bg-gray-200" />
                <div className="flex items-center gap-2 text-xs tracking-[0.12px] text-gray-600">
                  <LockSimpleIcon aria-hidden className="size-3.5" />
                  <span>Only owner can edit</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {error && hasTemplates ? (
          <div
            className="mt-4 flex items-center justify-between gap-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            role="alert"
          >
            <p>{error.message} Showing the last loaded templates.</p>
            {isRetryable ? (
              <Button variant="outline" size="sm" onPress={retry}>
                Try again
              </Button>
            ) : null}
          </div>
        ) : null}

        <div className="grid min-h-0 flex-1">
          <AnimatePresence initial={false}>
            <motion.div
              key={contentState}
              className="col-start-1 row-start-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={contentTransition}
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      <ScheduledBriefingsModals
        archiveTemplate={{
          template: templateToArchive,
          isArchiving: isChangingStatus,
          onArchive: (template) => void setTemplateArchived(template, true),
          onCancel: () => setTemplateToArchive(null),
        }}
      />
    </div>
  );
};

export default TemplatesLandingRoute;
